import type { VersionRepresentationType } from '@readme/api-contracts/schemas/version';

import cleanBranchName from '../cleanBranchName';

/**
 * Compute the lang-aware base name a branch should fork from for a given
 * version. New branches under a translation version live at
 * `<base>-<lang>_<suffix>` (e.g. `1.0-fr_my-feature`); English / non-i18n
 * versions just use `<base>_<suffix>`.
 *
 * Idempotent across the two real-world data shapes for translation
 * versions (we've seen both):
 *   - bare:  `name = '1.0'`,    `i18n.lang = 'fr'`
 *   - baked: `name = '1.0-fr'`, `i18n.lang = 'fr'`
 *
 * Both produce `'1.0-fr'` here. Without the `cleanBranchName` strip the
 * baked shape would double-tag (`'1.0-fr-fr'`) — that's the bug this
 * helper exists to prevent. Use it anywhere you'd otherwise write
 * `${name}${langSuffix}` to construct a branch base.
 *
 * @example getVersionBranchBase({ name: '1.0',    i18n: { lang: 'fr' } }, true)  // '1.0-fr'
 * @example getVersionBranchBase({ name: '1.0-fr', i18n: { lang: 'fr' } }, true)  // '1.0-fr'
 * @example getVersionBranchBase({ name: '1.0',    i18n: { lang: 'en' } }, true)  // '1.0'
 * @example getVersionBranchBase({ name: '1.0',    i18n: { lang: 'fr' } }, false) // '1.0'
 */
export default function getVersionBranchBase(
  version: Pick<VersionRepresentationType, 'i18n' | 'name'> | null | undefined,
  isI18nEnabled: boolean,
): string | null {
  if (!version) return null;
  const bareName = isI18nEnabled ? cleanBranchName(version.name) : version.name;
  const lang = version.i18n?.lang;
  const langSuffix = isI18nEnabled && lang && lang !== 'en' ? `-${lang}` : '';
  return `${bareName}${langSuffix}`;
}
